import { Button, Badge, cn } from '@design-system';
import { Plus, Settings, Trash2 } from 'lucide-react';
import {
  BuilderQuestionField,
  QuestionGroupSettingsDialog,
  useQuestionGroup,
  useFormQuestions,
  getFormBuilderActions,
} from '@/features/quote-and-apply-builder';

interface BuilderQuestionGroupProps {
  section: PolicyFormSection;
  subsection: PolicyFormSubSection;
  group: PolicyFormQuestionGroup;
}

export function BuilderQuestionGroup({ section, subsection, group }: BuilderQuestionGroupProps) {
  const questions = useFormQuestions();
  const { removeGroup } = getFormBuilderActions();
  const {
    showSettings,
    isSelected,
    hasCondition,
    formatClasses,
    handleClick,
    handleAddQuestion,
    handleSaveSettings,
    handleOpenSettings,
    handleCloseSettings,
  } = useQuestionGroup({ section, subsection, group });

  return (
    <div
      className={cn(
        'rounded-md border p-3 transition-all',
        isSelected
          ? 'border-primary-400 bg-primary-50/50 ring-primary-200 ring-1'
          : 'border-gray-100 bg-gray-50/50 hover:border-gray-200',
      )}
      onClick={handleClick}
    >
      <div className="mb-3 flex items-start gap-2">
        <div className="flex-1 space-y-1">
          <div
            className={cn(
              'text-base font-medium',
              group.title ? 'text-gray-950' : 'italic text-gray-400',
            )}
          >
            {group.title || 'Untitled Group'}
          </div>

          {group.description && <div className="text-sm text-gray-700">{group.description}</div>}

          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
            {group.format && group.format !== 'default' && (
              <Badge
                variant="secondary"
                className="capitalize"
              >
                {group.format}
              </Badge>
            )}
            {group.hidden && <Badge variant="warning">Hidden</Badge>}
            {hasCondition && <Badge variant="success">Conditions</Badge>}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleOpenSettings}
            title="Group Settings"
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={event => {
              event.stopPropagation();
              removeGroup(section.id, subsection.id, group.id);
            }}
            title="Delete Group"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className={cn(formatClasses, 'mb-3')}>
        {group.questions.map(questionRef => {
          const question = questions[questionRef];
          if (!question) return null;

          return (
            <BuilderQuestionField
              key={questionRef}
              section={section}
              subsection={subsection}
              group={group}
              question={question}
            />
          );
        })}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleAddQuestion}
      >
        <Plus className="mr-1 h-3 w-3" />
        Add Question
      </Button>

      <QuestionGroupSettingsDialog
        open={showSettings}
        group={group}
        questions={questions}
        onSave={handleSaveSettings}
        onClose={handleCloseSettings}
      />
    </div>
  );
}
